import React, { useRef, useState, useCallback, useEffect } from 'react';
import './VideoPlayer.css';

function fmt(s) {
  if (!s || isNaN(s)) return '0:00';
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
}

export default function VideoPlayer({ src, loop, playsInline, className, autoPlay, initialMuted }) {
  const videoRef = useRef(null);
  const wrapperRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(initialMuted ?? false);
  const [progress, setProgress] = useState(0);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = initialMuted ?? false;
    if (autoPlay) v.play().catch(() => {});
  }, [autoPlay, initialMuted]);

  const toggle = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.paused ? v.play() : v.pause();
  }, []);

  const seek = useCallback((e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v?.duration) return;
    const r = e.currentTarget.getBoundingClientRect();
    v.currentTime = ((e.clientX - r.left) / r.width) * v.duration;
  }, []);

  const toggleMute = useCallback((e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  }, []);

  const handleMeta = useCallback((e) => {
    const v = e.currentTarget;
    setDur(v.duration);
    if (v.videoHeight > v.videoWidth) {
      v.style.maxHeight = '100vh';
      v.style.width = 'auto';
      v.style.maxWidth = '100%';
      if (wrapperRef.current) {
        wrapperRef.current.style.width = 'fit-content';
        wrapperRef.current.style.maxWidth = '100%';
        if (wrapperRef.current.closest('.alt-left')) {
          wrapperRef.current.style.marginLeft = 'auto';
        }
      }
    }
  }, []);

  return (
    <div className={`vp${playing ? '' : ' vp-paused'}${className ? ' ' + className : ''}`} ref={wrapperRef}>
      <video
        ref={videoRef}
        src={src}
        loop={loop}
        playsInline={playsInline}
        webkit-playsinline="true"
        preload="metadata"
        onTimeUpdate={() => {
          const v = videoRef.current;
          if (v?.duration) { setTime(v.currentTime); setProgress(v.currentTime / v.duration); }
        }}
        onLoadedMetadata={handleMeta}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onClick={toggle}
      />
      <div className="vp-controls">
        <button className="vp-btn" onClick={(e) => { e.stopPropagation(); toggle(); }}>
          {playing ? <span className="vp-icon-pause" /> : <span className="vp-icon-play" />}
        </button>
        <div className="vp-bar" onClick={seek}>
          <div className="vp-fill" style={{ width: `${progress * 100}%` }} />
        </div>
        <span className="vp-time">{fmt(time)} / {fmt(dur)}</span>
        <button className="vp-btn" onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>
          {muted ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 9a5 5 0 0 1 .95 2.293" />
              <path d="M19.364 5.636a9 9 0 0 1 1.889 9.96" />
              <path d="m2 2 20 20" />
              <path d="m7 7-.587.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298V11" />
              <path d="M9.828 4.172A.686.686 0 0 1 11 4.657v.686" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" />
              <path d="M16 9a5 5 0 0 1 0 6" />
              <path d="M19.364 18.364a9 9 0 0 0 0-12.728" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
